'use client';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Checkbox } from '@shared/components/ui/checkbox';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Input } from '@shared/components/ui/shadcn/input';
import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { SimpleFilterField, SimpleFilterOption } from './types';

interface SimpleFilterContentProps {
  fields: SimpleFilterField[];
  values: Record<string, any>;
  onChange: (values: Partial<Record<string, any>>) => void;
  onReset?: () => void;
  isLoading?: boolean;
  loadingText?: string;
  showClearButton?: boolean;
  clearButtonText?: string;
  translations?: {
    searching?: string;
    clear?: string;
    selectDate?: string;
  };
}

/**
 * Componente SimpleFilterContent - Contenido de los filtros
 *
 * Componente interno que renderiza los campos de filtro sin el wrapper de modal.
 * Usado tanto en la versión inline como en la versión modal.
 */
export const SimpleFilterContent = ({
  fields,
  values,
  onChange,
  onReset,
  isLoading = false,
  loadingText = 'Buscando...',
  showClearButton = true,
  clearButtonText = 'Limpiar',
  translations = {},
}: SimpleFilterContentProps) => {
  // 🎯 Estado local para campos de texto (para evitar re-renders innecesarios)
  const [localTextValues, setLocalTextValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.type === 'text') {
        initial[field.key] = values[field.key] || '';
      }
    });
    return initial;
  });

  // 🎯 Sincronizar valores locales con valores externos
  useEffect(() => {
    const newLocalValues: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.type === 'text') {
        newLocalValues[field.key] = values[field.key] || '';
      }
    });
    setLocalTextValues((prev) => {
      // Solo actualizar si realmente cambió
      const changed = Object.keys(newLocalValues).some((key) => prev[key] !== newLocalValues[key]);
      return changed ? newLocalValues : prev;
    });
  }, [fields, values]);

  // 🎯 Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return fields.some((field) => {
      const value = values[field.key];
      if (value === undefined || value === null || value === '') return false;
      if (field.type === 'select' && field.allOption && value === field.allOption.value) return false;
      if (field.type === 'checkbox' && !value) return false;
      return true;
    });
  }, [fields, values]);

  // 🎯 Handlers para diferentes tipos de campos
  const handleTextChange = (key: string, value: string) => {
    setLocalTextValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleTextSearch = (field: SimpleFilterField) => {
    const value = localTextValues[field.key] || '';
    if (field.textConfig?.onSearch) {
      field.textConfig.onSearch(value);
    } else {
      onChange({ [field.key]: value || undefined });
    }
  };

  const handleTextKeyDown = (field: SimpleFilterField, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTextSearch(field);
    }
  };

  const handleSelectChange = (key: string, value: string, allOption?: SimpleFilterOption) => {
    onChange({
      [key]: value === allOption?.value ? undefined : value,
    });
  };

  const handleDateChange = (key: string, date: Date | undefined) => {
    onChange({
      [key]: date ? date.toLocaleDateString('en-CA') : undefined,
    });
  };

  const handleCheckboxChange = (key: string, checked: boolean) => {
    onChange({
      [key]: checked || undefined,
    });
  };

  const handleReset = () => {
    // Limpiar valores locales de texto
    const clearedTextValues: Record<string, string> = {};
    fields.forEach((field) => {
      if (field.type === 'text') {
        clearedTextValues[field.key] = '';
      }
    });
    setLocalTextValues(clearedTextValues);

    // Llamar callback de reset
    if (onReset) {
      onReset();
    } else {
      // Reset por defecto: limpiar todos los valores
      const clearedValues: Record<string, any> = {};
      fields.forEach((field) => {
        if (field.type === 'checkbox') {
          clearedValues[field.key] = false;
        } else {
          clearedValues[field.key] = undefined;
        }
      });
      onChange(clearedValues);
    }
  };

  // 🎯 Renderizar campo según su tipo
  const renderField = (field: SimpleFilterField) => {
    const value = values[field.key];
    const localTextValue = localTextValues[field.key] || '';

    switch (field.type) {
      case 'text': {
        const hasSearchButton = field.textConfig?.hasSearchButton ?? true;
        return (
          <div key={field.key} className="w-full">
            <label htmlFor={field.key} className="block text-xs font-medium text-muted-foreground mb-1">
              {field.label}
            </label>
            <div className="relative">
              <Input
                id={field.key}
                type="text"
                value={localTextValue}
                onChange={(e) => handleTextChange(field.key, e.target.value)}
                onKeyDown={(e) => handleTextKeyDown(field, e)}
                placeholder={field.placeholder}
                disabled={field.disabled}
                className={hasSearchButton ? 'pr-9' : ''}
              />
              {hasSearchButton && (
                <button
                  type="button"
                  onClick={() => handleTextSearch(field)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded transition-colors"
                  disabled={field.disabled}
                >
                  <Search className="h-4 w-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        );
      }

      case 'select': {
        const currentValue = value ? String(value) : field.allOption?.value || '';

        // 🎯 Si tiene searchFn, usar DynamicCombobox con scroll infinito
        if (field.searchFn) {
          return (
            <div key={field.key} className="w-full">
              <label htmlFor={field.key} className="block text-xs font-medium text-muted-foreground mb-1">
                {field.label}
              </label>
              <DynamicCombobox
                value={currentValue}
                setValue={(val) => handleSelectChange(field.key, val, field.allOption)}
                placeholder={
                  field.isLoadingOptions
                    ? translations.searching || 'Cargando...'
                    : field.placeholder || 'Seleccionar...'
                }
                searchFn={field.searchFn!}
                disabled={field.disabled || field.isLoadingOptions}
                dataTestId={`${field.key}-combobox`}
                emptyMessage="No se encontraron resultados"
                loadingMessage={translations.searching || 'Cargando...'}
                onLoadMore={field.onLoadMore}
                hasMore={field.hasMore ?? false}
              />
            </div>
          );
        }

        // 🎯 Si no tiene searchFn, usar Combobox estático
        const options = field.options || [];
        const allOptions = field.allOption ? [field.allOption, ...options] : options;

        return (
          <div key={field.key} className="w-full">
            <label htmlFor={field.key} className="block text-xs font-medium text-muted-foreground mb-1">
              {field.label}
            </label>
            <Combobox
              options={allOptions}
              placeholder={
                field.isLoadingOptions ? translations.searching || 'Cargando...' : field.placeholder || 'Seleccionar...'
              }
              value={currentValue}
              setValue={(val) => handleSelectChange(field.key, val, field.allOption)}
              disabled={field.disabled || field.isLoadingOptions}
              translations={{
                notFound: 'No se encontraron resultados',
              }}
            />
          </div>
        );
      }

      case 'date': {
        const dateValue = value ? new Date(value) : undefined;
        return (
          <div key={field.key} className="flex flex-col gap-1 w-full">
            <label className="text-xs text-muted-foreground">{field.label}</label>
            <DatePicker
              date={dateValue}
              onDateChange={(date) => handleDateChange(field.key, date)}
              placeholder={field.dateConfig?.placeholder || translations.selectDate || 'Seleccionar fecha'}
              disabled={field.disabled}
              className="h-9"
            />
          </div>
        );
      }

      case 'date-range': {
        // Para date-range, esperamos que el valor sea un objeto con 'from' y 'to'
        const rangeValue = value || {};
        const dateFrom = rangeValue.from ? new Date(rangeValue.from) : undefined;
        const dateTo = rangeValue.to ? new Date(rangeValue.to) : undefined;

        return (
          <div key={field.key} className="flex flex-col gap-2 w-full">
            <label className="text-xs text-muted-foreground">{field.label}</label>
            <div className="flex gap-2">
              <DatePicker
                date={dateFrom}
                onDateChange={(date) => {
                  onChange({
                    [field.key]: {
                      ...rangeValue,
                      from: date ? date.toLocaleDateString('en-CA') : undefined,
                    },
                  });
                }}
                placeholder={translations.selectDate || 'Desde'}
                disabled={field.disabled}
                className="h-9 flex-1"
              />
              <DatePicker
                date={dateTo}
                onDateChange={(date) => {
                  onChange({
                    [field.key]: {
                      ...rangeValue,
                      to: date ? date.toLocaleDateString('en-CA') : undefined,
                    },
                  });
                }}
                placeholder={translations.selectDate || 'Hasta'}
                disabled={field.disabled}
                className="h-9 flex-1"
              />
            </div>
          </div>
        );
      }

      case 'checkbox': {
        const checked = Boolean(value);
        return (
          <div key={field.key} className="flex flex-col gap-1 w-full">
            <label htmlFor={field.key} className="block text-xs font-medium text-muted-foreground">
              {field.label}
            </label>
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.key}
                checked={checked}
                onCheckedChange={(checked) => handleCheckboxChange(field.key, checked === true)}
                disabled={field.disabled}
                name={field.key}
              />
              <label htmlFor={field.key} className="text-sm text-foreground cursor-pointer">
                {field.checkboxConfig?.label || field.label}
              </label>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // 🎯 Organizar campos: separar fechas individuales para agruparlas
  const textFields = fields.filter((f) => f.type === 'text');
  const selectFields = fields.filter((f) => f.type === 'select');
  const dateFields = fields.filter((f) => f.type === 'date');
  const dateRangeFields = fields.filter((f) => f.type === 'date-range');
  const checkboxFields = fields.filter((f) => f.type === 'checkbox');

  // 🎯 Detectar si hay campos de fecha que deben ir juntos (date_from y date_to)
  const dateFromField = dateFields.find((f) => f.key.includes('from') || f.key.includes('date_from'));
  const dateToField = dateFields.find((f) => f.key.includes('to') || f.key.includes('date_to'));
  const otherDateFields = dateFields.filter((f) => f !== dateFromField && f !== dateToField);

  return (
    <div className="space-y-3">
      {/* Campos de texto - uno por fila */}
      {textFields.map(renderField)}

      {/* Campos select - uno por fila */}
      {selectFields.map(renderField)}

      {/* Campos de fecha agrupados (date_from y date_to juntos) */}
      {dateFromField && dateToField && (
        <div className="grid grid-cols-2 gap-3">
          {renderField(dateFromField)}
          {renderField(dateToField)}
        </div>
      )}

      {/* Otros campos de fecha individuales - uno por fila */}
      {otherDateFields.map(renderField)}

      {/* Campos date-range - uno por fila */}
      {dateRangeFields.map(renderField)}

      {/* Campos checkbox - uno por fila */}
      {checkboxFields.map(renderField)}

      {/* Indicador de carga */}
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{loadingText}</span>
        </div>
      )}

      {/* Botón de limpiar filtros */}
      {showClearButton && hasActiveFilters && (
        <div className="flex justify-end pt-3 border-t border-border">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors border border-border rounded-md hover:bg-accent h-9"
            title={clearButtonText}
          >
            <X className="h-4 w-4" />
            {clearButtonText}
          </button>
        </div>
      )}
    </div>
  );
};
